import type { OrganizationType } from '@kanbu/schema';
import { Currency, Role } from '@kanbu/schema/enums';
import { DateFormat, formatUtils } from '@kanbu/shared';
import { useLingui } from '@lingui/react/macro';
import { createColumnHelper } from '@tanstack/react-table';
import { Button, cn, Tooltip } from '@utima/ui';
import { AlertCircle, Repeat } from 'lucide-react';
import { useMemo } from 'react';

import { ActionsCell } from '@/components/dataTable/cells/ActionsCell';
import { useOrganizationChange } from '@/hooks/useOrganizationChange';
import { useUser } from '@/hooks/useUser';
import { isPartnerCapable } from '@/lib/routerUtils';
import { trpc } from '@/services/trpc';

import { useOrganizationMutation } from './useOrganizationMutation';

const columnHelper = createColumnHelper<OrganizationType>();

function OpenInvoiceWarning({ organizationId }: { organizationId: string }) {
  const { t } = useLingui();
  const { data: invoices } = trpc.subscriptions.listInvoices.useQuery(
    { organizationId, statuses: ['open', 'draft'] },
    {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  );

  const hasActionableInvoice = (invoices?.length ?? 0) > 0;

  if (!hasActionableInvoice) {
    return null;
  }

  return (
    <Tooltip title={t`Open or draft invoice requires manual action.`}>
      <span className='inline-flex text-amber-500'>
        <AlertCircle className='size-3.5' aria-hidden='true' />
      </span>
    </Tooltip>
  );
}

export function useOrganizationsCols() {
  const { t } = useLingui();
  const { user } = useUser();
  const {
    remove: { mutateAsync: remove },
  } = useOrganizationMutation();

  const handleOrganizationChange = useOrganizationChange();

  const isSuperAdmin = user?.role === Role.SuperAdmin;
  const partnerCapable = isPartnerCapable(user);

  return useMemo(
    () => [
      columnHelper.accessor(row => row.id, {
        id: 'id',
        header: t`ID`,
      }),
      columnHelper.accessor(row => row.name, {
        id: 'name',
        header: t`Name`,
        meta: {
          sortable: 'createdAt',
        },
      }),
      columnHelper.accessor(row => row.createdAt, {
        id: 'createdAt',
        header: t`Created`,
        cell: row => formatUtils.date(row.getValue(), DateFormat.DateTime),
        meta: {
          sortable: 'createdAt',
        },
      }),
      columnHelper.accessor(row => row.monthlyUsageLimit, {
        id: 'monthlyUsage',
        header: t`Monthly usage`,
        cell: ({ row }) => {
          const current = row.original.currentMonthlyUsage ?? 0;
          const limit = row.original.monthlyUsageLimit ?? 0;
          const orgCurrency = row.original.currency ?? Currency.USD;

          if (!limit) {
            return (
              <span className='font-semibold'>
                {formatUtils.credits(current, orgCurrency)}
              </span>
            );
          }

          return (
            <span
              className={cn(
                'inline-flex gap-1 items-baseline',
                current > limit ? 'text-red-600' : 'text-green-600',
              )}
            >
              <span className='font-semibold'>
                {formatUtils.credits(current, orgCurrency)}
              </span>
              /
              <span className='text-xs font-semibold'>
                {formatUtils.credits(limit, orgCurrency)}
              </span>
            </span>
          );
        },
        meta: {
          align: 'right',
          sortable: 'currentMonthlyUsage',
        },
      }),
      columnHelper.accessor(row => row.balance, {
        id: 'balance',
        header: t`Prepaid balance`,
        cell: ({ row }) => {
          const current = row.original.balance ?? 0;
          const orgCurrency = row.original.currency ?? Currency.USD;

          // The open-invoice probe calls `listInvoices`, which authorizes only
          // SuperAdmin or the partner that manages the client org. A partner's
          // list also contains their own hub (a partner org), which is not a
          // managed client — probing it would 403. Only render where billing is
          // actually manageable: SuperAdmin (any row) or partner (client rows,
          // never the hub).
          const canProbeInvoices =
            isSuperAdmin ||
            (partnerCapable && !row.original.isPartnerOrganization);

          return (
            <span className='inline-flex items-center gap-1'>
              <span
                className={cn(
                  'font-semibold',
                  current > 0 ? 'text-green-600' : '',
                )}
              >
                {formatUtils.credits(current, orgCurrency)}
              </span>
              {canProbeInvoices && (
                <OpenInvoiceWarning organizationId={row.original.id} />
              )}
            </span>
          );
        },
        meta: {
          align: 'right',
          sortable: 'balance',
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: t`Actions`,
        cell: ({ row }) => (
          <ActionsCell
            onDelete={() => remove({ id: row.original.id })}
            deleteDescription={t`Are you sure you want to delete this organization? Any remaining credits will be permanently lost.`}
            confirmationText={row.original.name}
            confirmationLabel={t`Type the organization name to confirm:`}
          >
            <Tooltip title={t`Switch to organization`}>
              <Button
                data-cta='admin-organizations-switch-to-organization-button'
                size='icon-xs'
                variant='ghost'
                className='text-blue-500'
                outline
                onClick={() => handleOrganizationChange(row.original.id)}
              >
                <Repeat className='size-4' />
              </Button>
            </Tooltip>
          </ActionsCell>
        ),
        meta: {
          align: 'center',
        },
      }),
    ],
    [remove, handleOrganizationChange, t, isSuperAdmin, partnerCapable],
  );
}
